import clsx from 'clsx'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import './styles.scss'

const Accordion = forwardRef(({
  className,
  trigger,
  content,
  duration = 300,
  easing = 'ease-out',
  triggerClassName,
  contentClassName,
  defaultOpen = false,
  arrowIcon = true,
  disabled = false,
  blocked = false,
  onClick,
  onToggle
}, ref) => {
  const [isActive, setIsActive] = useState((defaultOpen && !disabled) || false)
  const contentRef = useRef(null)
  const prevActive = useRef(defaultOpen && !disabled)

  const toggle = () => {
    if (disabled || blocked) return
      setIsActive(prev => {
        const newState = !prev;
        if (onToggle) {
            onToggle(newState);
        }
        return newState;
    });
  }

  useImperativeHandle(ref, () => ({
    open: () => !disabled && setIsActive(true),
    close: () => setIsActive(false),
    toggle: () => !disabled && setIsActive(prev => !prev),
    isOpen: () => isActive,
  }), [disabled, isActive])

  useEffect(() => {
    if (disabled) {
      setIsActive(false)
    }
  }, [disabled])

  useEffect(() => {
    if (onToggle && defaultOpen) {
      onToggle(defaultOpen);
    }
  }, [defaultOpen, onToggle])

  useEffect(() => {
    const $content = contentRef.current
    if (!$content || disabled) return

    const scrollHeight = $content.scrollHeight

    if (isActive) {
      $content.animate([{ height: '0px' }, { height: `${scrollHeight}px` }], {
        duration,
        easing,
      }).onfinish = () => {
        $content.style.height = 'auto'
      }
    } else if (prevActive.current) {
      $content.animate([{ height: `${scrollHeight}px` }, { height: '0px' }], {
        duration,
        easing,
      }).onfinish = () => {
        $content.style.height = '0px'
      }
    }

    prevActive.current = isActive
  }, [isActive, duration, easing, disabled])

  return (
    <div
      className={clsx('accordion', { '_disabled': disabled }, className)}
      onClick={onClick}
    >
      <div
        onClick={toggle}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        className={clsx(
          triggerClassName,
          'accordion__trigger',
          { '_active': isActive, '_disabled': disabled }
        )}
      >
        {arrowIcon && (
          <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
            <path
              d='M4.5 15.75L12 8.25L19.5 15.75'
              stroke='#1B1B1B'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        )}
        {trigger}
      </div>

      <div
        ref={contentRef}
        className={clsx(contentClassName, 'accordion__content')}
        style={{
          overflow: 'hidden',
          height: isActive ? 'auto' : '0px',
        }}
      >
        {content}
      </div>
    </div>
  )
})

export default Accordion
